import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { BlobProvider } from "@react-pdf/renderer";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/authContext";

import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import moment from "moment";
import "moment/locale/id";
import useKesimpulan from "../../services/queries/useKesimpulan";
import useUkm from "../../services/queries/useUkm";
import useUser from "../../services/queries/useUser";
import { getLocalUkmId } from "../../utils/handleChangeUkmId";
import toRupiah from "../../utils/toRupiah";

import { Close } from "@mui/icons-material";
import NumberFormat from "react-number-format";
import Swal from "sweetalert2";
import { textPrimary } from "../../constants/colors";
import { ParamsKesimpulan } from "../../constants/types";

import axios from "../../services/axios";
import useShiftNow from "../../services/queries/useShiftNow";
import PrintStrukKesimpulan from "../PrintStrukKesimpulan/PrintStrukKesimpulan";

interface IModalEndShiftProps {
    fullScreen: boolean;
    isOpenModalEndShift: boolean;
    closeModalEndShift: () => void;
}

function hexToRgb(hex: string | undefined): string {
    if (!hex) return "255, 255, 255";

    hex = hex.replace(/^#/, "");

    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }

    const num = parseInt(hex, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;

    return `${r}, ${g}, ${b}`;
}

const ModalEndShift = ({
    fullScreen,
    isOpenModalEndShift,
    closeModalEndShift,
}: IModalEndShiftProps) => {
    const { data: user } = useUser();

    const theme = useTheme();
    const isTabletScreen = useMediaQuery(theme.breakpoints.between("md", "lg"));

    let now = moment().format("YYYY-MM-DD");

    const [isButtonLoading, setIsButtonLoading] = useState(false);

    const initialParamsUkm = {
        size: 500,
        page: 1,
        search: "",
    };
    const { data: ukm } = useUkm(initialParamsUkm);
    const { ukmIdUser } = useAuth();
    const ukmId = React.useMemo(() => {
        if (user?.userId) {
            return getLocalUkmId(user?.userId);
        }
        return null;
    }, [user?.userId]);

    const ukmData = React.useMemo(() => {
        if (ukmId) {
            return ukm?.content.find((li) => li.id === ukmId);
        }
        return ukm?.content[0];
    }, [ukm?.content, ukmId]);

    const initialParamsGetShiftNow = {
        ukmId: ukmIdUser && ukmIdUser[0],
        tipe: "now",
    };

    const { data: shiftNow, refetch: refetchShiftNow } = useShiftNow(
        initialParamsGetShiftNow,
    );

    const [kasAkhir, setKasAkhir] = useState<number>(0);
    const [catatan, setCatatan] = useState<string>("");

    const [state, setState] = useState({
        isBarangTerjual: false,
    });

    useEffect(() => {
        if (ukmData?.webBarangTerjualTutupBuku === 1) {
            setState((prev) => ({
                ...prev,
                isBarangTerjual: true,
            }));
        }
    }, [ukmData]);

    const initialParamsKesimpulan = {
        ukmId: undefined,
        tanggal: now,
        grupProduk: 1,
        isShift: 1,
    };
    const [paramsKesimpulan, setParamsKesimpulan] = useState<ParamsKesimpulan>(
        initialParamsKesimpulan,
    );

    const { data: kesimpulan, refetch: refetchKesimpulan } =
        useKesimpulan(paramsKesimpulan);

    useEffect(() => {
        if (ukmIdUser) {
            setParamsKesimpulan((prev) => ({
                ...prev,
                ukmId: ukmIdUser && ukmIdUser[0],
            }));
        }
    }, [ukmIdUser]);

    useEffect(() => {
        if (shiftNow?.id) {
            setParamsKesimpulan((prev) => ({
                ...prev,
                shiftId: shiftNow?.id,
            }));
        }
    }, [shiftNow]);

    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
    const [isWaitingForData, setIsWaitingForData] = useState(false);

    const endShift = async () => {
        setIsButtonLoading(true);
        try {
            const body = {
                shiftId: shiftNow?.id,
                kasAkhir: kasAkhir,
                catatan: catatan,
            };

            const { data } = await axios.post(`/api/shift-end`, body);
            if (data.code === 200) {
                await Swal.fire({
                    title: "Shift berhasil diakhiri",
                    position: "top-end",
                    showConfirmButton: false,
                    icon: "success",
                    toast: true,
                    timer: 3000,
                    timerProgressBar: true,
                    showCloseButton: true,
                    customClass: {
                        container: "my-swal",
                    },
                });

                setIsWaitingForData(true);
                await refetchKesimpulan();

                await new Promise((resolve) => setTimeout(resolve, 3000));
                setIsWaitingForData(false);

                setIsGeneratingPdf(true);
            }

            refetchShiftNow();
            closeModalEndShift();
        } catch (error) {
            // if (defaultAxios.isAxiosError(error)) {
            //     const serverError = error as AxiosError<
            //         ErrorFieldResponse | undefined
            //     >;
            // }
            console.error(error);
            setIsButtonLoading(false);
            setIsWaitingForData(false);
        }
    };

    useEffect(() => {
        if (pdfBlob) {
            const pdfUrl = URL.createObjectURL(pdfBlob);

            const link = document.createElement("a");
            link.href = pdfUrl;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => {
                URL.revokeObjectURL(pdfUrl);
                setIsButtonLoading(false);
                setIsGeneratingPdf(false);
            }, 100);
        }
    }, [pdfBlob]);

    return (
        <Dialog
            maxWidth="md"
            fullScreen={fullScreen}
            fullWidth={true}
            open={isOpenModalEndShift}
            scroll="body"
            onClose={() => {
                closeModalEndShift();
            }}
            PaperProps={{
                sx: {
                    borderRadius: 1,
                },
            }}
        >
            <DialogTitle>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                >
                    <Typography fontSize={32} fontWeight="bold">
                        Akhiri Shift
                    </Typography>
                    <IconButton
                        onClick={() => {
                            closeModalEndShift();
                        }}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Box
                    bgcolor={ukmData?.warna}
                    padding={2}
                    borderRadius={4}
                    sx={{
                        backgroundColor: `rgba(${hexToRgb(
                            ukmData?.warna,
                        )}, 0.2)`,
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography color={textPrimary.body}>
                                Kas Awal
                            </Typography>

                            <NumberFormat
                                value={toRupiah(shiftNow?.kasAwal ?? 0)}
                                inputMode="numeric"
                                customInput={TextField}
                                thousandSeparator="."
                                decimalSeparator=","
                                fullWidth
                                placeholder="0"
                                allowNegative={false}
                                disabled
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                            >
                                                Rp.
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        "& .MuiInputBase-input.Mui-disabled": {
                                            WebkitTextFillColor: "#000",
                                            cursor: "default",
                                        },
                                    },
                                }}
                                inputProps={{
                                    inputMode: "numeric",
                                    style: {
                                        fontSize: 16,
                                        fontWeight: "bold",
                                        textAlign: "left",
                                        cursor: "default",
                                    },
                                    readOnly: true,
                                }}
                                sx={{
                                    marginTop: 1,
                                    marginBottom: 2,
                                    backgroundColor: "#FFF",
                                    borderRadius: 2,
                                    "& .Mui-disabled": {
                                        backgroundColor:
                                            "rgba(255, 255, 255, 0.8)",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography color={textPrimary.body}>
                                Pendapatan
                            </Typography>

                            <NumberFormat
                                value={toRupiah(shiftNow?.pendapatan ?? 0)}
                                inputMode="numeric"
                                customInput={TextField}
                                thousandSeparator="."
                                decimalSeparator=","
                                fullWidth
                                placeholder="0"
                                allowNegative={false}
                                disabled
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                            >
                                                Rp.
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        "& .MuiInputBase-input.Mui-disabled": {
                                            WebkitTextFillColor: "#000",
                                            cursor: "default",
                                        },
                                    },
                                }}
                                inputProps={{
                                    inputMode: "numeric",
                                    style: {
                                        fontSize: 16,
                                        fontWeight: "bold",
                                        textAlign: "left",
                                        cursor: "default",
                                    },
                                    readOnly: true,
                                }}
                                sx={{
                                    marginTop: 1,
                                    marginBottom: 2,
                                    backgroundColor: "#FFF",
                                    borderRadius: 2,
                                    "& .Mui-disabled": {
                                        backgroundColor:
                                            "rgba(255, 255, 255, 0.8)",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography color={textPrimary.body}>
                                Pengeluaran
                            </Typography>

                            <NumberFormat
                                value={toRupiah(shiftNow?.pengeluaran ?? 0)}
                                inputMode="numeric"
                                customInput={TextField}
                                thousandSeparator="."
                                decimalSeparator=","
                                fullWidth
                                placeholder="0"
                                allowNegative={false}
                                disabled
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                            >
                                                Rp.
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        "& .MuiInputBase-input.Mui-disabled": {
                                            WebkitTextFillColor: "#000",
                                            cursor: "default",
                                        },
                                    },
                                }}
                                inputProps={{
                                    inputMode: "numeric",
                                    style: {
                                        fontSize: 16,
                                        fontWeight: "bold",
                                        textAlign: "left",
                                        cursor: "default",
                                    },
                                    readOnly: true,
                                }}
                                sx={{
                                    marginTop: 1,
                                    marginBottom: 2,
                                    backgroundColor: "#FFF",
                                    borderRadius: 2,
                                    "& .Mui-disabled": {
                                        backgroundColor:
                                            "rgba(255, 255, 255, 0.8)",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography color={textPrimary.body}>
                                Kas Akhir
                            </Typography>

                            <NumberFormat
                                value={toRupiah(shiftNow?.kasAkhir ?? 0)}
                                inputMode="numeric"
                                customInput={TextField}
                                thousandSeparator="."
                                decimalSeparator=","
                                fullWidth
                                placeholder="0"
                                allowNegative={false}
                                disabled
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                            >
                                                Rp.
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        "& .MuiInputBase-input.Mui-disabled": {
                                            WebkitTextFillColor: "#000",
                                            cursor: "default",
                                        },
                                    },
                                }}
                                inputProps={{
                                    inputMode: "numeric",
                                    style: {
                                        fontSize: 16,
                                        fontWeight: "bold",
                                        textAlign: "left",
                                        cursor: "default",
                                    },
                                    readOnly: true,
                                }}
                                sx={{
                                    marginTop: 1,
                                    marginBottom: 2,
                                    backgroundColor: "#FFF",
                                    borderRadius: 2,
                                    "& .Mui-disabled": {
                                        backgroundColor:
                                            "rgba(255, 255, 255, 0.8)",
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Box mt={2}>
                    <Typography fontSize={18} fontWeight={500}>
                        Total Kas Akhir Kasir (Aktual)
                    </Typography>

                    <NumberFormat
                        value={kasAkhir === null ? "" : kasAkhir}
                        inputMode="numeric"
                        customInput={TextField}
                        thousandSeparator="."
                        decimalSeparator=","
                        fullWidth
                        placeholder="0"
                        allowNegative={false}
                        onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                            setKasAkhir(+event.target.value.replace(/\./g, ""));
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Typography variant="h6" fontWeight="bold">
                                        Rp.
                                    </Typography>
                                </InputAdornment>
                            ),
                        }}
                        inputProps={{
                            inputMode: "numeric",
                            style: {
                                fontSize: 16,
                                fontWeight: "bold",
                                textAlign: "left",
                            },
                        }}
                        sx={{
                            marginTop: 1,
                            marginBottom: 2,
                        }}
                    />

                    <Typography color={textPrimary.body}>
                        Total kas akhir tidak bisa di ubah setelah di akhiri
                        shift harap masukan nominal yang sesuai
                    </Typography>
                </Box>

                <Box mt={2}>
                    <Typography fontSize={18} fontWeight={500}>
                        Catatan
                    </Typography>

                    <TextField
                        label="Masukan Catatan"
                        multiline
                        rows={2}
                        fullWidth
                        value={catatan}
                        onChange={(e) => setCatatan(e.target.value)}
                        sx={{
                            mt: 1,
                        }}
                    />
                </Box>

                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    gap={1}
                    mt={3}
                    flex={1}
                >
                    <Button
                        variant="outlined"
                        onClick={() => {
                            closeModalEndShift();
                        }}
                    >
                        Batal
                    </Button>
                    <Box>
                        {isGeneratingPdf ? (
                            <BlobProvider
                                document={
                                    <PrintStrukKesimpulan
                                        namaToko={
                                            shiftNow?.kantungParkir.nama ??
                                            "Toko"
                                        }
                                        alamatToko={
                                            ukmData?.alamatToko ?? "Toko"
                                        }
                                        telpToko={ukmData?.noTelp ?? "Toko"}
                                        gambarToko={
                                            ukmData?.gambarTokoBW === null
                                                ? undefined
                                                : ukmData?.gambarTokoBW
                                        }
                                        tanggalTransaksi={moment(
                                            paramsKesimpulan.tanggal,
                                        ).format("DD MMMM YYYY")}
                                        html={undefined}
                                        kesimpulan={kesimpulan ?? []}
                                        isBarangTerjual={state.isBarangTerjual}
                                    />
                                }
                            >
                                {({ blob, url, loading, error }) => {
                                    if (blob && !loading && !error) {
                                        setPdfBlob(blob);
                                    }
                                    return null;
                                }}
                            </BlobProvider>
                        ) : (
                            <LoadingButton
                                loading={isButtonLoading || isWaitingForData}
                                loadingPosition="center"
                                variant="contained"
                                onClick={endShift}
                                size={isTabletScreen ? "small" : "large"}
                                disabled={kasAkhir === null}
                                fullWidth
                            >
                                {isWaitingForData
                                    ? "Akhiri Shift"
                                    : "Akhiri Shift"}
                            </LoadingButton>
                        )}
                    </Box>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default ModalEndShift;
